import React, { useState, useEffect, useCallback, useMemo } from 'react'

import { IconButton } from '@mui/material';
import CloseIcon from "@mui/icons-material/Close"
import { Dialog, Button } from '@mui/material'
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';

import SelectInput from '../components/SelectInput';
import LabelInput from '../components/LabelInput'

import { isNumber } from '../../../utils/utils'
import { mapFlagName, mapAtomicFlagName, mapRangeEdgeName, mapDocumentComparisonName, mapExpectedFlagStateName, mapRiskClassificationName, ocrFlags, ocrAtomicFlags, isAtomicOcrFlag, livenessFlags, riskClassificationLevels, fallbackDecisions, floorComparisonTypes, ceilingComparisonTypes, documentComparisonTypes, expectedFlagStates, DEFAULT_RISK_CLASSIFICATION_LEVEL, DEFAULT_INCONCLUSIVE_FLOOR, DEFAULT_INCONCLUSIVE_CEILING, DEFAULT_DOCUMENT_SIMILARITY_THRESHOLD, DEFAULT_FLOOR_COMPARISON_TYPE, DEFAULT_CEILING_COMPARISON_TYPE, DEFAULT_DOCUMENT_COMPARISON_TYPE, DEFAULT_FALLBACK_DECISION } from '../rules_utils';


function RuleCreationDialog(props) {
    const { open, setOpen, handleToast, submitRule, submitType, editingRule } = props

    const [parameters, setParameters] = useState({})
    const [parametersErrors, setParametersErrors] = useState({
        'limiter': false,
        'floor': false,
        'ceiling': false,
        'documentThreshold': false
    })

    const ruleOptions = useMemo(
        () => [
            {
                value: 'bureau_fraud_occurrences',
                name: 'Consulta de fraudes em Bureau',
                description: 'Caso a classificação de risco de fraude do assinante em Bureau seja igual ou superior ao nível selecionado.',
                type: 'risk_level'
            },
            {
                value: 'fraud_face_matches',
                name: 'Banco de fraudes',
                description: 'Caso o assinante tenha face encontrada no banco de fraudes.',
                type: 'boolean'
            },
            {
                value: 'liveness',
                name: 'Liveness',
                description: 'Assinante foi identificado como fraudador na etapa de liveness',
                type: 'boolean'
            },
            {
                value: 'brazil_geolocation',
                name: 'Assinatura fora do Brasil',
                description: 'Caso o assinante tenha feito a assinatura fora do Brasil.',
                type: 'boolean'
            },
            {
                value: 'device_key_blocklist',
                name: 'Lista de dispositivos bloqueados',
                description: 'Caso dispositivo consta na Lista de dispositivos bloqueados.',
                type: 'boolean'
            },
            {    
                value: 'same_ip_different_document_numbers',
                name: 'Mesmo IP, diferentes CPFs',
                description: 'Se existirem outros processos de assinaturas no mesmo IP para número de documentos diferentes nos últimos 30 dias.',
                type: 'boolean'
            },
            {
                value: 'facematch_document',
                name: 'Face Match de Documento',
                description: 'Pontuação de checagem de simlaridade de face com documento - OCR.',
                type: 'numeric'
            },
            {
                value: 'face_validation',
                name: 'Validação de Faces',
                description: 'Pontuação de validação se a foto do rosto confere com o CPF informado.',
                type: 'numeric'
            },
            {
                value: 'liveness_flags',
                name: 'Liveness - Indicadores',
                description: 'Lista de indicadores detectados durante a etapa de liveness.',
                type: 'list_presence'
            },
            {
                value: 'ocr_flags',
                name: 'OCR - Indicadores',
                description: 'Lista de indicadores detectados durante a etapa de OCR.',
                type: 'list_presence'
            },
            {
                value: 'face_validation_document_fallback',
                name: 'Revalidação de biometria por documento',
                description: 'Quando o score de validação de face fica na faixa inconclusiva, a biometria é comparada com a foto do documento pessoal enviado pelo assinante, desde que os indicadores de OCR estejam nos estados exigidos.',
                type: 'composite'
            }
        ], [])

    const [ruleOption, setRuleOption] = useState({
        value: 'fraud_face_matches',
        name: 'Banco de fraudes',
        description: 'Caso o assinante tenha face encontrada no banco de fraudes.',
        type: 'boolean'
    })

    const repprovementDecisions = useMemo(
        () => [
            {
                value: 'reproved',
                name: 'Reprovar'
            },
            {
                value: 'in_manual_analysis',
                name: 'Análise manual'
            }
        ], []
    )

    const ocrFlagsOptions = useMemo(
        () => ocrFlags, []
    )

    const livenessFlagsOptions = useMemo(
        () => livenessFlags, []
    )

    const riskLevelOptions = useMemo(
        () => riskClassificationLevels, []
    )

    const ocrAtomicFlagsOptions = useMemo(
        () => ocrAtomicFlags, []
    )

    const fallbackDecisionOptions = useMemo(
        () => fallbackDecisions, []
    )

    const floorComparisonOptions = useMemo(
        () => floorComparisonTypes, []
    )

    const ceilingComparisonOptions = useMemo(
        () => ceilingComparisonTypes, []
    )

    const documentComparisonOptions = useMemo(
        () => documentComparisonTypes, []
    )

    const expectedFlagStateOptions = useMemo(
        () => expectedFlagStates, []
    )

    const [repprovementDecision, setRepprovementDecision] = useState({
        value: 'reproved',
        name: 'Reprovar'
    })

    const [riskLevel, setRiskLevel] = useState({
        value: DEFAULT_RISK_CLASSIFICATION_LEVEL,
        name: mapRiskClassificationName(DEFAULT_RISK_CLASSIFICATION_LEVEL)
    })

    const [flagOption, setFlagOption] = useState({})
    const [selectedFlags, setSelectedFlags] = useState([])

    const [fallbackDecision, setFallbackDecision] = useState(
        fallbackDecisions.find(opt => opt.value === DEFAULT_FALLBACK_DECISION)
    )

    const [floorComparison, setFloorComparison] = useState(
        floorComparisonTypes.find(opt => opt.value === DEFAULT_FLOOR_COMPARISON_TYPE)
    )

    const [ceilingComparison, setCeilingComparison] = useState(
        ceilingComparisonTypes.find(opt => opt.value === DEFAULT_CEILING_COMPARISON_TYPE)
    )

    const [documentComparison, setDocumentComparison] = useState(
        documentComparisonTypes.find(opt => opt.value === DEFAULT_DOCUMENT_COMPARISON_TYPE)
    )

    const [requiredFlagOption, setRequiredFlagOption] = useState({})
    const [requiredFlags, setRequiredFlags] = useState([])

    const handleRuleInputChange = useCallback(
        (field, value) => {
            setParameters((prev) => ({
                ...prev,
                [field]: value
            }))
        },
        []
    )

    const handleBlur = (id, value) => {
        const error = validateInput[id](value);
        setParametersErrors((prev) => ({
            ...prev,
            [id]: error
        }))
    }

    const validateInput = {
        limiter: (value) => {
            return value === "" || !isNumber(value) || (Number(value) >= 100) || (Number(value) < 1)
        },
        floor: (value) => {
            return value === "" || !isNumber(value) || (Number(value) >= 100) || (Number(value) < 1)
        },
        ceiling: (value) => {
            return value === "" || !isNumber(value) || (Number(value) >= 100) || (Number(value) < 1)
        },
        documentThreshold: (value) => {
            return value === "" || !isNumber(value) || (Number(value) >= 100) || (Number(value) < 1)
        }
    }

    const compositeFieldsAreInvalid = () => {
        return validateInput.floor(parameters.floor ?? '') || validateInput.ceiling(parameters.ceiling ?? '') || validateInput.documentThreshold(parameters.documentThreshold ?? '')
    }

    const inconclusiveRangeIsInverted = () => {
        return Number(parameters.floor) > Number(parameters.ceiling)
    }

    const inconclusiveRangeIsEmpty = () => {
        if (Number(parameters.floor) !== Number(parameters.ceiling)) {
            return false
        }
        return floorComparison.value === 'greater' || ceilingComparison.value === 'less'
    }

    const highlightCompositeFieldErrors = () => {
        setParametersErrors((prev) => ({
            ...prev,
            'floor': validateInput.floor(parameters.floor ?? ''),
            'ceiling': validateInput.ceiling(parameters.ceiling ?? ''),
            'documentThreshold': validateInput.documentThreshold(parameters.documentThreshold ?? '')
        }))
    }

    const handleRuleOptionChange = useCallback(
        (value) => {
            let rule = ruleOptions.find(opt => opt.value === value)
            if (rule) {
                setRuleOption(rule)
                setSelectedFlags([])
                setRequiredFlags([])
                setRequiredFlagOption({})
                if (rule.type === 'composite') {
                    setParameters((prev) => ({
                        ...prev,
                        'floor': prev.floor ?? DEFAULT_INCONCLUSIVE_FLOOR,
                        'ceiling': prev.ceiling ?? DEFAULT_INCONCLUSIVE_CEILING,
                        'documentThreshold': prev.documentThreshold ?? DEFAULT_DOCUMENT_SIMILARITY_THRESHOLD
                    }))
                }
                return rule
            } else {
                return
            }
        }, [ruleOptions]
    )

    const handleRepprovementDecisionChange = useCallback(
        (value) => {
            setRepprovementDecision(repprovementDecisions.find(opt => opt.value === value))
        }, [repprovementDecisions]
    )

    const handleRiskLevelChange = useCallback(
        (value) => {
            let level = riskLevelOptions.find(opt => opt.value === value)
            if (level) {
                setRiskLevel(level)
            }
        }, [riskLevelOptions]
    )

    const removeFlagItem = (value) => {
        setSelectedFlags((prev) => (prev.filter((item) => item.value !== value)))
    }

    const handleFallbackDecisionChange = useCallback(
        (value) => {
            let decision = fallbackDecisionOptions.find(opt => opt.value === value)
            if (decision) {
                setFallbackDecision(decision)
            }
        }, [fallbackDecisionOptions]
    )

    const handleFloorComparisonChange = useCallback(
        (value) => {
            let comparison = floorComparisonOptions.find(opt => opt.value === value)
            if (comparison) {
                setFloorComparison(comparison)
            }
        }, [floorComparisonOptions]
    )

    const handleCeilingComparisonChange = useCallback(
        (value) => {
            let comparison = ceilingComparisonOptions.find(opt => opt.value === value)
            if (comparison) {
                setCeilingComparison(comparison)
            }
        }, [ceilingComparisonOptions]
    )

    const handleDocumentComparisonChange = useCallback(
        (value) => {
            let comparison = documentComparisonOptions.find(opt => opt.value === value)
            if (comparison) {
                setDocumentComparison(comparison)
            }
        }, [documentComparisonOptions]
    )

    const handleRequiredFlagOptionChange = (value) => {
        if (requiredFlags.find(requiredFlag => requiredFlag.value === value)) {
            handleToast('Este indicador já foi adicionado', 'warning')
            setRequiredFlagOption({})
            return
        }

        let flag = ocrAtomicFlagsOptions.find(opt => opt.value === value)
        if (flag) {
            setRequiredFlags((prev) => ([...prev, { value: flag.value, name: flag.name, expected: 'false' }]))
        }
        setRequiredFlagOption({})
    }

    const handleRequiredFlagExpectedChange = (flagValue, expected) => {
        setRequiredFlags((prev) => (prev.map((item) => {
            if (item.value === flagValue) {
                return { ...item, expected: expected }
            }
            return item
        })))
    }

    const removeRequiredFlagItem = (value) => {
        setRequiredFlags((prev) => (prev.filter((item) => item.value !== value)))
    }

    const handleFlagOptionChange = useCallback(
        (value, list) => {
            if (list === 'ocr_flags') {
                if (selectedFlags.length > 0) {
                    if (selectedFlags.find(selectedFlag => selectedFlag.value === value)) {
                        setFlagOption({})
                        return
                    } else {
                        setSelectedFlags((prev) => ([...prev, ocrFlagsOptions.find(opt => opt.value === value)]))
                    }
                } else {
                    setSelectedFlags((prev) => ([...prev, ocrFlagsOptions.find(opt => opt.value === value)]))
                }
            } else if (list === 'liveness_flags') {
                if (selectedFlags.length > 0) {
                    if (selectedFlags.find(selectedFlag => selectedFlag.value === value)) {
                        setFlagOption({})
                        return
                    } else {
                        setSelectedFlags((prev) => ([...prev, livenessFlagsOptions.find(opt => opt.value === value)]))
                    }
                } else {
                    setSelectedFlags((prev) => ([...prev, livenessFlagsOptions.find(opt => opt.value === value)]))
                }
            }
            setFlagOption({})
        }, [ocrFlagsOptions, livenessFlagsOptions, selectedFlags]
    )

    const handleAvailableFlagOptions = (list) => {
        if (list === 'ocr_flags') {
            return ocrFlagsOptions
        } else if (list === 'liveness_flags') {
            return livenessFlagsOptions
        }

        return [];
    };
    const mapRule = () => {
        const rule = {
            step: '',
            type: '',
            parameters: [

            ]
        }

        rule.step = ruleOption.value
        rule.type = ruleOption.type

        switch (rule.type) {
            case 'boolean':
                if ([
                    'fraud_face_matches',
                    'liveness',
                    'brazil_geolocation',
                    'same_ip_different_document_numbers',
                    'device_key_blocklist'
                ].includes(rule.step)) {
                    rule.parameters.push(
                        {
                            decision: repprovementDecision.value
                        }
                    )
                } else {
                    return
                }
                break
            case 'numeric':
                if ([
                    'facematch_document', 
                    'face_validation'
                ].includes(rule.step)) {
                    rule.parameters.push(
                        {
                            comparison_type: "less_or_equal",
                            decision: repprovementDecision.value,
                            value: parameters.limiter
                        }
                    )
                } else {
                    return
                }
                break
            case 'risk_level':
                if ([
                    'bureau_fraud_occurrences'
                ].includes(rule.step)) {
                    rule.parameters.push(
                        {
                            comparison_type: "greater_or_equal",
                            decision: repprovementDecision.value,
                            value: riskLevel.value
                        }
                    )
                } else {
                    return
                }
                break
            case 'list_presence':
                if ([
                    'ocr_flags',
                    'liveness_flags'
                ].includes(rule.step)) {
                    rule.parameters.push(
                        {
                            decision: repprovementDecision.value,
                            value: selectedFlags.map(flag => flag.value)
                        }
                    )
                } else {
                    return
                }
                break
            case 'composite':
                if ([
                    'face_validation_document_fallback'
                ].includes(rule.step)) {
                    rule.parameters.push(
                        {
                            decision: repprovementDecision.value,
                            value: [String(parameters.floor), String(parameters.ceiling)],
                            floor_comparison_type: floorComparison.value,
                            ceiling_comparison_type: ceilingComparison.value,
                            comparison_type: documentComparison.value,
                            fallback_decision: fallbackDecision.value,
                            document_similarity_threshold: String(parameters.documentThreshold),
                            required_ocr_flags: requiredFlags.map((flag) => ({ flag: flag.value, expected: flag.expected === 'true' }))
                        }
                    )
                } else {
                    return
                }
                break
            default:
                return
        }

        return rule
    }

    const resetForm = useCallback(
        () => {
            setParametersErrors({
                'limiter': false,
                'floor': false,
                'ceiling': false,
                'documentThreshold': false
            })
            setRepprovementDecision({
                value: 'reproved',
                name: 'Reprovar'
            })
            setRiskLevel({
                value: DEFAULT_RISK_CLASSIFICATION_LEVEL,
                name: mapRiskClassificationName(DEFAULT_RISK_CLASSIFICATION_LEVEL)
            })
            setFallbackDecision(fallbackDecisions.find(opt => opt.value === DEFAULT_FALLBACK_DECISION))
            setFloorComparison(floorComparisonTypes.find(opt => opt.value === DEFAULT_FLOOR_COMPARISON_TYPE))
            setCeilingComparison(ceilingComparisonTypes.find(opt => opt.value === DEFAULT_CEILING_COMPARISON_TYPE))
            setDocumentComparison(documentComparisonTypes.find(opt => opt.value === DEFAULT_DOCUMENT_COMPARISON_TYPE))
            switch (ruleOption.value) {
                case 'facematch_document':
                    setParameters((prev) => {
                        const new_value = {
                            ...prev,
                            'limiter': '80'
                        }
                        return new_value
                    })
                    break
                case 'face_validation':
                    setParameters((prev) => {
                        const new_value = {
                            ...prev,
                            'limiter': '80'
                        }
                        return new_value
                    })
                    break
                case 'ocr_flags':
                    setSelectedFlags([])
                    break
                case 'liveness_flags':
                    setSelectedFlags([])
                    break
                case 'face_validation_document_fallback':
                    setParameters((prev) => {
                        const new_value = {
                            ...prev,
                            'floor': DEFAULT_INCONCLUSIVE_FLOOR,
                            'ceiling': DEFAULT_INCONCLUSIVE_CEILING,
                            'documentThreshold': DEFAULT_DOCUMENT_SIMILARITY_THRESHOLD
                        }
                        return new_value
                    })
                    setRequiredFlags([])
                    setRequiredFlagOption({})
                    break
                default:
                    break
            }
        },
        [ruleOption, setParametersErrors]
    )
    useEffect(() => {
        if (!editingRule) {
            resetForm()
        }
    }, [ruleOption, editingRule, resetForm])


    useEffect(() => {
        if (editingRule) {
            let newRuleOption = handleRuleOptionChange(editingRule.step)

            if (!newRuleOption) {
                return
            }

            let key = ''
            let value = null
            let decision = 'reproved'

            if (newRuleOption.type === 'numeric') {
                value = editingRule.parameters[0].value
                key = 'limiter'
                handleRuleInputChange(key, value)
            } else if (newRuleOption.type === 'list_presence') {
                value = editingRule.parameters[0].value
                setSelectedFlags(value.map((flag) => ({ value: flag, name: mapFlagName(flag) })))
            } else if (newRuleOption.type === 'risk_level') {
                value = editingRule.parameters[0].value ?? DEFAULT_RISK_CLASSIFICATION_LEVEL
                handleRiskLevelChange(value)
            } else if (newRuleOption.type === 'composite') {
                const editingParameters = editingRule.parameters[0]
                const editingRange = editingParameters.value ?? []
                handleRuleInputChange('floor', editingRange[0] ?? DEFAULT_INCONCLUSIVE_FLOOR)
                handleRuleInputChange('ceiling', editingRange[1] ?? DEFAULT_INCONCLUSIVE_CEILING)
                handleRuleInputChange('documentThreshold', editingParameters.document_similarity_threshold ?? DEFAULT_DOCUMENT_SIMILARITY_THRESHOLD)
                handleFloorComparisonChange(editingParameters.floor_comparison_type ?? DEFAULT_FLOOR_COMPARISON_TYPE)
                handleCeilingComparisonChange(editingParameters.ceiling_comparison_type ?? DEFAULT_CEILING_COMPARISON_TYPE)
                handleDocumentComparisonChange(editingParameters.comparison_type ?? DEFAULT_DOCUMENT_COMPARISON_TYPE)
                handleFallbackDecisionChange(editingParameters.fallback_decision ?? DEFAULT_FALLBACK_DECISION)
                setRequiredFlags((editingParameters.required_ocr_flags ?? []).filter((item) => isAtomicOcrFlag(item.flag)).map((item) => {
                    if (item.expected) {
                        return { value: item.flag, name: mapAtomicFlagName(item.flag), expected: 'true' }
                    }
                    return { value: item.flag, name: mapAtomicFlagName(item.flag), expected: 'false' }
                }))
            }

            decision = editingRule.parameters[0].decision
            handleRepprovementDecisionChange(decision)
        }
    }, [editingRule, handleRepprovementDecisionChange, handleRuleInputChange, handleRuleOptionChange, handleRiskLevelChange, handleFloorComparisonChange, handleCeilingComparisonChange, handleDocumentComparisonChange, handleFallbackDecisionChange])

    return (
        <>
            <Dialog
                open={open}
                slotProps={{
                    backdrop: {
                        style: { backgroundColor: 'rgba(0, 0, 0, 0.5)' }
                    }
                }}
            >
                <div style={{ width: '400px', padding: '2rem' }}>
                    <IconButton
                        aria-label="Close"
                        onClick={() => {
                            setOpen(false)
                            resetForm()
                        }}
                        style={{ float: "right" }}
                    >
                        <CloseIcon />
                    </IconButton>
                    <h1 style={{ marginBottom: '2rem' }}>Criação de regra</h1>
                    <div>
                        <h2 style={{ marginBottom: '10px' }}>Selecione qual regra deseja adicionar:</h2>
                        <SelectInput
                            optionSelected={ruleOption}
                            items={ruleOptions}
                            valueKey="value"
                            labelKey="name"
                            onValueChange={handleRuleOptionChange}
                        />
                    </div>
                    <div style={{ marginBottom: '20px', marginTop: '10px' }}>
                        <p style={{ marginBottom: '5px', fontSize: '12px' }}><span><b>Descrição</b></span></p>
                        <p style={{ marginBottom: '5px', fontSize: '12px' }}>{ruleOption.description}</p>
                    </div>

                    {
                        ruleOption.type === 'boolean' &&
                        <>
                            <div>
                                <h2 style={{ marginBottom: '10px' }}>Decisão final:</h2>
                                <SelectInput
                                    optionSelected={repprovementDecision}
                                    items={repprovementDecisions}
                                    valueKey="value"
                                    labelKey="name"
                                    onValueChange={handleRepprovementDecisionChange}
                                />
                            </div>
                        </>
                    }

                    {
                        ruleOption.type === 'risk_level' &&
                        <>
                            <div>
                                <div>
                                    <h2 style={{ marginBottom: '10px' }}>Nível de risco:</h2>
                                    <SelectInput
                                        optionSelected={riskLevel}
                                        items={riskLevelOptions}
                                        valueKey="value"
                                        labelKey="name"
                                        onValueChange={handleRiskLevelChange}
                                    />
                                </div>
                                <div>
                                    <h2 style={{ marginBottom: '10px' }}>Decisão final:</h2>
                                    <SelectInput
                                        optionSelected={repprovementDecision}
                                        items={repprovementDecisions}
                                        valueKey="value"
                                        labelKey="name"
                                        onValueChange={handleRepprovementDecisionChange}
                                    />
                                </div>
                            </div>

                            <div style={{ marginBottom: '10px', marginTop: '30px' }}>
                                <h2 style={{ marginBottom: '10px' }}>Resumo:</h2>

                                <p style={{ marginBottom: '5px', fontSize: '12px' }}>
                                    <span><b>{repprovementDecision.name}</b>, se o risco for igual ou superior a <b>{riskLevel.name}</b></span>
                                </p>
                            </div>
                        </>
                    }

                    {
                        ruleOption.type === 'numeric' &&
                        <>
                            <div>
                                <div>
                                    <LabelInput
                                        labelValue="Limitador"
                                        value={parameters.limiter || ''}
                                        onChange={(value) => { handleRuleInputChange('limiter', value) }}
                                        onBlur={(value) => handleBlur("limiter", value)}
                                        error={parametersErrors['limiter']}
                                        errorMessage={parametersErrors['limiter'] ? 'Adicione um número válido entre 1 e 99' : ''}
                                    />
                                </div>
                                <div>
                                    <h2 style={{ marginBottom: '10px' }}>Decisão final:</h2>
                                    <SelectInput
                                        optionSelected={repprovementDecision}
                                        items={repprovementDecisions}
                                        valueKey="value"
                                        labelKey="name"
                                        onValueChange={handleRepprovementDecisionChange}
                                    />
                                </div>
                            </div>

                            <div style={{ marginBottom: '10px', marginTop: '30px' }}>
                                <h2 style={{ marginBottom: '10px' }}>Resumo:</h2>

                                <p style={{ marginBottom: '5px', fontSize: '12px' }}>
                                    <span><b>{repprovementDecision.name}</b>, se pontuação menor ou igual a <b>{parameters.limiter}</b></span>
                                </p>
                            </div>
                        </>

                    }

                    {
                        ruleOption.type === 'list_presence' &&
                        <>
                            <div>
                                <div>
                                    <h2 style={{ marginBottom: '10px' }}>Indicadores:</h2>
                                    <SelectInput
                                        optionSelected={flagOption}
                                        items={handleAvailableFlagOptions(ruleOption.value)}
                                        valueKey="value"
                                        labelKey="name"
                                        onValueChange={(value) => handleFlagOptionChange(value, ruleOption.value)}
                                    />
                                </div>
                                <div>
                                    <h2 style={{ marginBottom: '10px' }}>Decisão final:</h2>
                                    <SelectInput
                                        optionSelected={repprovementDecision}
                                        items={repprovementDecisions}
                                        valueKey="value"
                                        labelKey="name"
                                        onValueChange={handleRepprovementDecisionChange}
                                    />
                                </div>
                            </div>

                            <div style={{ marginBottom: '10px', marginTop: '30px' }}>
                                <h2 style={{ marginBottom: '10px' }}>Resumo:</h2>

                                <p style={{ marginBottom: '5px', fontSize: '12px' }}>
                                    <span><b>{repprovementDecision.name}</b>, se os indicadores abaixo forem detectados:</span>
                                    <ul style={{ marginTop: '15px' }}>
                                        {
                                            selectedFlags.map((flag) => (
                                                <li key={flag.value} style={{ marginLeft: '30px', marginTop: '5px' }}>
                                                    <div style={{ width: '100%', padding: '5px', display: 'flex', justifyContent: 'space-between' }}>
                                                        <span className="data-field__value">
                                                            {mapFlagName(flag.value)}
                                                        </span>
                                                        <IconButton
                                                            aria-label="Close"
                                                            onClick={() => removeFlagItem(flag.value)}
                                                            style={{ float: "right", height: '15px' }}
                                                        >
                                                            <RemoveCircleOutlineIcon style={{ color: 'red' }} />
                                                        </IconButton>
                                                    </div>
                                                </li>
                                            ))
                                        }
                                    </ul>
                                </p>
                            </div>
                        </>
                    }

                    {
                        ruleOption.type === 'composite' &&
                        <>
                            <div>
                                <h2 style={{ marginBottom: '10px' }}>Faixa inconclusiva de score:</h2>
                                <div style={{ display: 'flex', gap: '10px', alignItems: 'flex-start' }}>
                                    <div style={{ flex: 1 }}>
                                        <LabelInput
                                            labelValue="De"
                                            value={parameters.floor || ''}
                                            onChange={(value) => { handleRuleInputChange('floor', value) }}
                                            onBlur={(value) => handleBlur('floor', value)}
                                            error={parametersErrors['floor']}
                                            errorMessage={parametersErrors['floor'] ? 'Adicione um número válido entre 1 e 99' : ''}
                                        />
                                    </div>
                                    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '.2rem' }}>
                                        <label style={{ fontSize: '15px', paddingBottom: '10px', fontWeight: 'bold', lineHeight: '1.3rem' }}>Borda:</label>
                                        <SelectInput
                                            optionSelected={floorComparison}
                                            items={floorComparisonOptions}
                                            valueKey="value"
                                            labelKey="name"
                                            width="100%"
                                            onValueChange={handleFloorComparisonChange}
                                        />
                                    </div>
                                </div>
                                <div style={{ display: 'flex', gap: '10px', alignItems: 'flex-start' }}>
                                    <div style={{ flex: 1 }}>
                                        <LabelInput
                                            labelValue="Até"
                                            value={parameters.ceiling || ''}
                                            onChange={(value) => { handleRuleInputChange('ceiling', value) }}
                                            onBlur={(value) => handleBlur('ceiling', value)}
                                            error={parametersErrors['ceiling']}
                                            errorMessage={parametersErrors['ceiling'] ? 'Adicione um número válido entre 1 e 99' : ''}
                                        />
                                    </div>
                                    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '.2rem' }}>
                                        <label style={{ fontSize: '15px', paddingBottom: '10px', fontWeight: 'bold', lineHeight: '1.3rem' }}>Borda:</label>
                                        <SelectInput
                                            optionSelected={ceilingComparison}
                                            items={ceilingComparisonOptions}
                                            valueKey="value"
                                            labelKey="name"
                                            width="100%"
                                            onValueChange={handleCeilingComparisonChange}
                                        />
                                    </div>
                                </div>
                                <p style={{ marginBottom: '20px', fontSize: '12px' }}>
                                    <span>Score de <b>{parameters.floor}</b> ({mapRangeEdgeName(floorComparison.value)}) a <b>{parameters.ceiling}</b> ({mapRangeEdgeName(ceilingComparison.value)}) aciona a revalidação por documento.</span>
                                </p>
                            </div>

                            <div style={{ marginBottom: '20px' }}>
                                <h2 style={{ marginBottom: '10px' }}>Similaridade mínima com o documento:</h2>
                                <div style={{ display: 'flex', gap: '10px', alignItems: 'flex-start' }}>
                                    <div style={{ flex: 1 }}>
                                        <LabelInput
                                            labelValue="Valor"
                                            value={parameters.documentThreshold || ''}
                                            onChange={(value) => { handleRuleInputChange('documentThreshold', value) }}
                                            onBlur={(value) => handleBlur('documentThreshold', value)}
                                            error={parametersErrors['documentThreshold']}
                                            errorMessage={parametersErrors['documentThreshold'] ? 'Adicione um número válido entre 1 e 99' : ''}
                                        />
                                    </div>
                                    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '.2rem' }}>
                                        <label style={{ fontSize: '15px', paddingBottom: '10px', fontWeight: 'bold', lineHeight: '1.3rem' }}>Comparação:</label>
                                        <SelectInput
                                            optionSelected={documentComparison}
                                            items={documentComparisonOptions}
                                            valueKey="value"
                                            labelKey="name"
                                            width="100%"
                                            onValueChange={handleDocumentComparisonChange}
                                        />
                                    </div>
                                </div>
                            </div>

                            <div style={{ marginBottom: '20px' }}>
                                <h2 style={{ marginBottom: '10px' }}>Indicadores de OCR considerados:</h2>
                                <SelectInput
                                    optionSelected={requiredFlagOption}
                                    items={ocrAtomicFlagsOptions}
                                    valueKey="value"
                                    labelKey="name"
                                    onValueChange={handleRequiredFlagOptionChange}
                                />
                                {
                                    requiredFlags.length === 0 &&
                                    <p style={{ marginBottom: '10px', fontSize: '12px' }}>
                                        <span>Nenhum indicador selecionado. A regra exige ao menos um.</span>
                                    </p>
                                }
                                <ul style={{ listStyle: 'none', margin: 0, padding: 0, marginBottom: '10px' }}>
                                    {
                                        requiredFlags.map((flag) => (
                                            <li key={flag.value} style={{ marginTop: '8px' }}>
                                                <div style={{ width: '100%', display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                    <span className="data-field__value" style={{ flex: 1, fontSize: '12px', lineHeight: '16px' }}>
                                                        {flag.name}
                                                    </span>
                                                    <div style={{ marginBottom: '-12px' }}>
                                                        <SelectInput
                                                            optionSelected={expectedFlagStateOptions.find(opt => opt.value === flag.expected)}
                                                            items={expectedFlagStateOptions}
                                                            valueKey="value"
                                                            labelKey="name"
                                                            width="90px"
                                                            onValueChange={(value) => handleRequiredFlagExpectedChange(flag.value, value)}
                                                        />
                                                    </div>
                                                    <IconButton
                                                        aria-label="Remover indicador"
                                                        onClick={() => removeRequiredFlagItem(flag.value)}
                                                        style={{ padding: '4px' }}
                                                    >
                                                        <RemoveCircleOutlineIcon style={{ color: 'red' }} />
                                                    </IconButton>
                                                </div>
                                            </li>
                                        ))
                                    }
                                </ul>
                                <p style={{ marginBottom: '20px', fontSize: '12px' }}>
                                    <span>Sim = o indicador precisa estar presente. Não = precisa estar ausente.</span>
                                </p>
                            </div>

                            <div style={{ marginBottom: '10px' }}>
                                <h2 style={{ marginBottom: '10px' }}>Se a revalidação passar:</h2>
                                <SelectInput
                                    optionSelected={fallbackDecision}
                                    items={fallbackDecisionOptions}
                                    valueKey="value"
                                    labelKey="name"
                                    onValueChange={handleFallbackDecisionChange}
                                />
                            </div>

                            <div>
                                <h2 style={{ marginBottom: '10px' }}>Se a revalidação não passar:</h2>
                                <SelectInput
                                    optionSelected={repprovementDecision}
                                    items={repprovementDecisions}
                                    valueKey="value"
                                    labelKey="name"
                                    onValueChange={handleRepprovementDecisionChange}
                                />
                            </div>

                            <div style={{ marginBottom: '10px', marginTop: '30px' }}>
                                <h2 style={{ marginBottom: '10px' }}>Resumo:</h2>

                                <div style={{ marginBottom: '5px', fontSize: '12px' }}>
                                    <span><b>{fallbackDecision.name}</b>, se o score de validação de face estiver entre <b>{parameters.floor}</b> ({mapRangeEdgeName(floorComparison.value)}) e <b>{parameters.ceiling}</b> ({mapRangeEdgeName(ceilingComparison.value)}), a similaridade com o documento for <b>{mapDocumentComparisonName(documentComparison.value)} {parameters.documentThreshold}</b> e os indicadores abaixo estiverem nos estados exigidos:</span>
                                    <ul style={{ marginTop: '15px' }}>
                                        {
                                            requiredFlags.map((flag) => (
                                                <li key={flag.value} style={{ marginLeft: '30px', marginTop: '5px' }}>
                                                    <span className="data-field__value">
                                                        {flag.name} — {mapExpectedFlagStateName(flag.expected === 'true')}
                                                    </span>
                                                </li>
                                            ))
                                        }
                                    </ul>
                                </div>
                                <p style={{ marginTop: '15px', fontSize: '12px' }}>
                                    <span>Caso contrário, <b>{repprovementDecision.name}</b>.</span>
                                </p>
                            </div>
                        </>
                    }

                    <div style={{ alignItems: 'center', textAlign: 'center' }}>
                        <Button
                            variant="contained"
                            color="primary"
                            onClick={() => {
                                if (Object.values(parametersErrors).find(error => error)) {
                                    handleToast('Existem campos mal preenchidos', 'warning')
                                } else if (ruleOption.type === 'list_presence' && selectedFlags.length === 0) {
                                    handleToast('É necessário selecionar pelo menos um indicador', 'warning')

                                } else if (ruleOption.type === 'composite' && compositeFieldsAreInvalid()) {
                                    highlightCompositeFieldErrors()
                                    handleToast('Existem campos mal preenchidos', 'warning')
                                } else if (ruleOption.type === 'composite' && inconclusiveRangeIsInverted()) {
                                    handleToast('O piso da faixa inconclusiva não pode ser maior que o teto', 'warning')
                                } else if (ruleOption.type === 'composite' && inconclusiveRangeIsEmpty()) {
                                    handleToast('A faixa inconclusiva ficou vazia. Ajuste os valores ou as bordas.', 'warning')
                                } else if (ruleOption.type === 'composite' && requiredFlags.length === 0) {
                                    handleToast('É necessário selecionar pelo menos um indicador', 'warning')
                                } else {
                                    let rule = mapRule()
                                    submitRule(rule, submitType)
                                }
                            }}
                            style={{ width: '100%', margin: '2rem 0', backgroundColor: 'primaryColor' }}
                        >
                            {submitType === 'add' ? 'Adicionar' : 'Editar'}
                        </Button>
                    </div>
                </div>
            </Dialog>
        </>
    )
};

export default RuleCreationDialog;